import { MESSAGE_TAG } from "@/schemas";
import type { MessageDiscussion } from "@/types";
import type { ValueOf } from "type-fest";

export function extractMentionedUserIdsFromText(
	text: string,
	availableUsers: { id: number; name: string }[],
) {
	const sortedUsers = [...availableUsers].sort(
		(a, b) => b.name.length - a.name.length,
	);
	const mentionedIds = new Set<number>();

	for (const user of sortedUsers) {
		const escapedName = user.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const mentionRegex = new RegExp(`(^|\\s)@${escapedName}(?=\\s|$)`, "g");

		if (mentionRegex.test(text)) {
			mentionedIds.add(user.id);
		}
	}

	return [...mentionedIds];
}

const escapeRegExp = (string: string) =>
	string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const transformMentions = (
	text: string,
	availableUsers: { id: number; name: string }[],
	removeUsers: number[] = [],
) => {
	if (!text || !availableUsers.length) return text;
	const sortedUsers = [...availableUsers].sort(
		(a, b) => b.name.length - a.name.length,
	);

	let result = text;

	for (const user of sortedUsers) {
		const escapedName = user.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const mentionRegex = new RegExp(
			`(^|\\s)@?(${escapedName})(?=\\s|[.,!?;:]|$)`,
			"g",
		);

		result = result.replace(mentionRegex, (_, prefix, name) =>
			removeUsers.includes(user.id)
				? `${prefix}`
				: `${prefix}<a href="/dashboard/contacts/member/${user.id}">${name}</a>`,
		);
	}

	return result;
};

export const highlightMarkdown = (text: string, keyword?: string) => {
	if (!keyword?.trim()) return text;

	const regex = new RegExp(`(${escapeRegExp(keyword)})`, "gi");
	return text.replace(regex, "<mark>$1</mark>");
};

export const collectMessagesTags = (messages: MessageDiscussion[]) => {
	const tags = messages
		.filter((f) => !!f.documents?.length || !!f.tags)
		.reduce(
			(acc, cur) => {
				if (cur.documents?.length) {
					const docTag = cur.documents[0].tags as any;
					const isFiles = [
						MESSAGE_TAG.FILES,
						MESSAGE_TAG.RESULTS,
						MESSAGE_TAG.IMAGING,
						MESSAGE_TAG.PHOTOS,
						MESSAGE_TAG.OTHER,
					].includes(docTag);
					if (isFiles) {
						acc.add(docTag);
						acc.add(MESSAGE_TAG.FILES);
					}
					if (!isFiles) acc.add(docTag);
				}
				const isExternal = [
					MESSAGE_TAG.REFERAL,
					MESSAGE_TAG.HANDOVER,
					MESSAGE_TAG.SPECIALISTS_ADVICE,
				].includes(cur.tags as any);
				if (isExternal) acc.add(MESSAGE_TAG.EXTERNAL);
				if (!isExternal && cur.tags) acc.add(cur.tags);
				return acc;
			},
			new Set([]) as Set<ValueOf<typeof MESSAGE_TAG>>,
		);

	return Array.from(tags);
};

const safeProtocol = /^(https?|ircs?|mailto|sms|tel|xmpp)$/i;

/**
 * Make a URL safe.
 *
 * This follows how GitHub works.
 * It allows the protocols `http`, `https`, `irc`, `ircs`, `mailto`, `sms`, `tel`, and `xmpp`,
 * and URLs relative to the current protocol (such as `/something`).
 * @see {@link https://github.com/remarkjs/react-markdown/blob/fda7fa560bec901a6103e195f9b1979dab543b17/lib/index.js#L425C1-L425C45}
 */
export function defaultUrlTransform(value: string) {
	// Same as:
	// <https://github.com/micromark/micromark/blob/929275e/packages/micromark-util-sanitize-uri/dev/index.js#L34>
	// But without the `encode` part.
	const colon = value.indexOf(":");
	const questionMark = value.indexOf("?");
	const numberSign = value.indexOf("#");
	const slash = value.indexOf("/");

	if (
		// If there is no protocol, it’s relative.
		colon === -1 ||
		// If the first colon is after a `?`, `#`, or `/`, it’s not a protocol.
		(slash !== -1 && colon > slash) ||
		(questionMark !== -1 && colon > questionMark) ||
		(numberSign !== -1 && colon > numberSign) ||
		// It is a protocol, it should be allowed.
		safeProtocol.test(value.slice(0, colon))
	) {
		return value;
	}

	return "";
}
